import React, { useMemo } from 'react';
import { Box, TextField, Alert, Typography, Chip } from '@mui/material';

import { validateScript } from 'components/ImpactAiDrawer/hooks/methods/parseData/scriptValidator';

import { MethodEditorProps } from '../types';

export const ParseDataEditor: React.FC<MethodEditorProps> = ({ payload, onChange }) => {
  const normalizedPayload = {
    script: '',
    ...payload,
  };

  // Real-time script validation
  const validation = useMemo(() => {
    if (!normalizedPayload.script) {
      return { isValid: true, errors: [], warnings: [] };
    }

    return validateScript(normalizedPayload.script, {
      maxLength: 10000,
      maxLines: 500,
      allowAsync: true,
      enforceObjectReturn: true,
    });
  }, [normalizedPayload.script]);

  return (
    <Box>
      <TextField
        fullWidth
        multiline
        rows={8}
        value={normalizedPayload.script}
        onChange={(e) => onChange({ ...normalizedPayload, script: e.target.value })}
        label="Parser Code"
        placeholder="function processData(context, ui) {&#10;  // Your data parsing logic here&#10;  return context.items.filter(item => item.score > 50);&#10;}"
        sx={{ fontFamily: 'monospace', mb: 2 }}
        helperText="Write JavaScript code. Define a 'processData' function for your data parsing logic."
        error={!validation.isValid}
      />

      {/* Validation feedback */}
      {normalizedPayload.script && (
        <Box sx={{ mb: 2 }}>
          {validation.errors.length > 0 && (
            <Alert severity="error" sx={{ mb: 1 }}>
              <Typography variant="body2" component="div">
                <strong>Security Issues:</strong>
                <ul style={{ margin: '4px 0', paddingLeft: '20px' }}>
                  {validation.errors.map((error) => (
                    <li key={error}>{error}</li>
                  ))}
                </ul>
              </Typography>
            </Alert>
          )}

          {validation.warnings.length > 0 && (
            <Alert severity="warning" sx={{ mb: 1 }}>
              <Typography variant="body2" component="div">
                <strong>Warnings:</strong>
                <ul style={{ margin: '4px 0', paddingLeft: '20px' }}>
                  {validation.warnings.map((warning) => (
                    <li key={warning}>{warning}</li>
                  ))}
                </ul>
              </Typography>
            </Alert>
          )}

          {validation.isValid && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Chip label="Script Valid" color="success" size="small" variant="outlined" />
              <Typography variant="caption" color="text.secondary">
                {normalizedPayload.script.length} characters,{' '}
                {normalizedPayload.script.split('\n').length} lines
              </Typography>
            </Box>
          )}
        </Box>
      )}

      <Alert severity="info" sx={{ mt: 2 }}>
        <Typography variant="body2">
          <strong>Parser Features:</strong>
          <br />• Access context data and transform it with JavaScript
          <br />• Use UI methods for interactive data processing:
          <br />
          <br />
          <code>await ui.showChoice(question, choices)</code>
          <br />
          Returns the selected choice value
          <br />
          Example:{' '}
          <code>
            const method = await ui.showChoice(&apos;How to score?&apos;, [&apos;distance&apos;,
            &apos;efficiency&apos;, &apos;impact&apos;])
          </code>
          <br />
          <br />
          <code>await ui.showInput(question, placeholder?)</code>
          <br />
          Returns the user&apos;s text input
          <br />
          Example:{' '}
          <code>
            const maxDistance = await ui.showInput(&apos;Max distance?&apos;, &apos;25 miles&apos;)
          </code>
          <br />
          <br />
          <code>ui.showMessage(message)</code>
          <br />
          Displays a message to the user (no return value)
          <br />
          Example: <code>ui.showMessage(&apos;Processing complete!&apos;)</code>
        </Typography>
      </Alert>

      <Alert severity="warning" sx={{ mt: 1 }}>
        <Typography variant="body2">
          <strong>Security:</strong> Scripts are validated and executed in isolation. Network
          access, DOM manipulation, and dangerous APIs are blocked for security.
        </Typography>
      </Alert>
    </Box>
  );
};
